import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { NotificationService, Notification, NotificationPreferences } from '@/lib/notifications';
import { toast } from '@/hooks/use-toast';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  preferences: NotificationPreferences | null;
  loading: boolean;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  updatePreferences: (prefs: Partial<NotificationPreferences>) => void;
  refreshNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshNotifications = async () => {
    try {
      setLoading(true);
      const notifs = await NotificationService.getUserNotifications();
      const prefs = NotificationService.getUserPreferences();
      
      setNotifications(notifs);
      setUnreadCount(NotificationService.getUnreadCount(notifs));
      setPreferences(prefs);
    } catch (error) {
      console.error('Error refreshing notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = (id: string) => {
    const success = NotificationService.markAsRead(id);
    if (success) {
      setNotifications(prev =>
        prev.map(notif => notif.id === id ? { ...notif, read: true } : notif)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  const markAllAsRead = () => {
    const success = NotificationService.markAllAsRead(notifications);
    if (success) {
      setNotifications(prev =>
        prev.map(notif => ({ ...notif, read: true }))
      );
      setUnreadCount(0);
    }
  };

  const updatePreferences = (prefs: Partial<NotificationPreferences>) => {
    const success = NotificationService.updatePreferences(prefs);
    if (success) {
      setPreferences(prev => prev ? { ...prev, ...prefs } : null);
      toast({
        title: "Preferences Updated",
        description: "Your notification preferences have been saved.",
      });
    } else {
      toast({
        title: "Error",
        description: "Failed to update notification preferences.",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    refreshNotifications();

    // Subscribe to real-time notifications
    const subscription = NotificationService.subscribeToRealtime(
      (newNotification) => {
        setNotifications(prev => [newNotification, ...prev]);
        setUnreadCount(prev => prev + 1);

        // Show toast for new notification
        toast({
          title: newNotification.title,
          description: newNotification.message,
        });
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    preferences,
    loading,
    markAsRead,
    markAllAsRead,
    updatePreferences,
    refreshNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications(): NotificationContextType {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}